// Service module to handle driver addition logic and related validation
// Keeps business logic separate from the UI component
export const validateDriver = (driver) => {
    const errors = {};

    if (!driver.name) errors.name = 'Driver name is required';
    if (!/^[0-9]{10}$/.test(driver.phone)) errors.phone = 'Phone must be exactly 10 digits';
    if (!driver.id_no) errors.id_no = 'Identification number is required';
    if (!driver.status) errors.status = 'Status is required';
    if (!driver.license_expiry) errors.license_expiry = 'License expiry is required';

    return errors;
};

// Verifies a Google Drive link by calling the provided apiCall function
// apiCall should have signature: async (method, endpoint, data)
export const verifyDriveLink = async (apiCall, url) => {
    try {
        const response = await apiCall('POST', '/utils/verify_google_drive_folder', { url });
        return response.data?.success
            ? { valid: true }
            : { valid: false, error: response.data?.message || 'Invalid Google Drive folder link' };
    } catch (error) {
        console.error('Drive verification error (service):', error);
        return { valid: false, error: 'Failed to verify drive link' };
    }
};

// Add driver business logic separated from component state management
// Returns an object describing the result. Component is responsible for updating local state/UI.
export const addDriver = async (apiCall, newDriver, user) => {
    // Validation
    const validationErrors = validateDriver(newDriver);
    if (Object.keys(validationErrors).length > 0) {
        return { success: false, validationErrors };
    }

    const driveLink = typeof newDriver.drive_link === 'string' ? newDriver.drive_link.trim() : newDriver.drive_link;
    if (driveLink) {
        const driveVerification = await verifyDriveLink(apiCall, driveLink);
        if (!driveVerification.valid) {
            return { success: false, driveError: driveVerification.error };
        }
    }

    try {
        const timestamp = new Date().toISOString();

        // Trim whitespace from all string values in newDriver
        const trimmedDriver = Object.keys(newDriver).reduce((acc, key) => {
            acc[key] = typeof newDriver[key] === 'string' ? newDriver[key].trim() : newDriver[key];
            return acc;
        }, {});

        const driverData = {
            ...trimmedDriver,
            input_by: user?.username || trimmedDriver.input_by,
            input_date: timestamp,
            license_expiry: trimmedDriver.license_expiry || null
        };

        const response = await apiCall('POST', '/drivers', driverData);

        const addedDriver = {
            ...driverData,
            id: response.data?.id || response.data?.data?.id || null,
            edit_date: timestamp
        };

        return { success: true, addedDriver };
    } catch (error) {
        console.error('Error adding driver (service):', error);
        const errorMsg = error?.response?.data?.error || error?.message || 'Error adding driver';
        return { success: false, error: errorMsg };
    }
};

const addDriverService = {
    validateDriver,
    verifyDriveLink,
    addDriver
};
export default addDriverService;
